import { PLATFORM_ROLE_TYPE } from '@/consts/common';
import { PlatformRoleType } from '@/services/users/types';
import { getCurrentUuid } from '@/utils/helpers/authHelpers';
import {
  getActionQualifierForRole,
  getUiViewAccessForRole,
  mergeAccessQualifiers,
  ORG_OWNER_ASSIGNABLE_ROLES,
  PLATFORM_ADMIN_ASSIGNABLE_ROLES,
  PLATFORM_SUPPORT_ASSIGNABLE_ROLES,
} from './policies';
import { getViewerRoles } from './roles';
import {
  IAuthRole,
  IAccessQualifier,
  IPermissionAction,
  IUiView,
  IUiViewAccess,
  IViewerContext,
  PERMISSION_ACTION,
} from './types';

const {
  ORG_ADMIN,
  ORG_OWNER,
  PLATFORM_AUDITOR,
  PLATFORM_SUPPORT,
  PLATFORM_ADMIN,
} = PLATFORM_ROLE_TYPE;

/** Merges action qualifiers across all viewer roles and returns the strongest one. */
function getMergedActionQualifier(
  action: IPermissionAction,
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IAccessQualifier {
  const qualifiers = viewerRoles.map((role) => getActionQualifierForRole(action, role));
  return mergeAccessQualifiers(qualifiers);
}

/** Merges UI view access levels across all viewer roles. */
function getMergedUiViewAccess(
  view: IUiView,
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IUiViewAccess {
  const accessLevels = viewerRoles.map((role) => getUiViewAccessForRole(view, role));

  if (accessLevels.includes('full')) return 'full';
  if (accessLevels.includes('scoped')) return 'scoped';
  if (accessLevels.includes('limited')) return 'limited';
  return 'none';
}

/** True when the viewer holds org-admin or org-owner (org-scoped roles). */
function viewerHasOrgScopedRole(viewerRoles: IAuthRole[]): boolean {
  return viewerRoles.includes(ORG_ADMIN) || viewerRoles.includes(ORG_OWNER);
}

/** True when the viewer holds any platform-level role (admin, support, or auditor). */
function viewerHasPlatformRole(viewerRoles: IAuthRole[]): boolean {
  return viewerRoles.includes(PLATFORM_ADMIN)
    || viewerRoles.includes(PLATFORM_SUPPORT)
    || viewerRoles.includes(PLATFORM_AUDITOR);
}

/** True when the target organization is within the viewer's org scope. */
function isScopedToViewerOrganization(context: IViewerContext): boolean {
  if (!context.organizationUuid || !context.viewerOrganizationUuid) {
    return viewerHasOrgScopedRole(getViewerRoles());
  }

  return context.organizationUuid === context.viewerOrganizationUuid;
}

/** True when the target record belongs to the current viewer. */
function isSelfTarget(context: IViewerContext): boolean {
  const currentUserUuid = getCurrentUuid();
  if (context.isViewingOwnRecord) return true;
  if (!context.targetUserUuid || !currentUserUuid) return false;
  return context.targetUserUuid === currentUserUuid;
}

/** Resolves whether a scoped permission applies for the given context. */
function appliesScopedPermission(context: IViewerContext = {}): boolean {
  const viewerRoles = getViewerRoles();

  if (viewerHasPlatformRole(viewerRoles)) {
    return true;
  }

  return isScopedToViewerOrganization(context);
}

/** Resolves whether a self-only permission applies for the given context. */
function appliesSelfPermission(context: IViewerContext = {}): boolean {
  return isSelfTarget(context);
}

/**
 * True when platform auditor is the viewer's only elevated role (no org admin/owner).
 * Org-scoped roles still grant write access when combined with platform auditor.
 */
export function isReadOnlyViewer(viewerRoles: IAuthRole[] = getViewerRoles()): boolean {
  const elevatedRoles = viewerRoles.filter((role) => role !== PLATFORM_ROLE_TYPE.USER);
  return elevatedRoles.length === 1 && elevatedRoles[0] === PLATFORM_AUDITOR;
}

/** Checks if the viewer may perform a specific action, optionally scoped by context. */
export function canPerformAction(
  action: IPermissionAction,
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): boolean {
  const mergedQualifier = getMergedActionQualifier(action, viewerRoles);

  if (mergedQualifier === 'none') return false;
  if (mergedQualifier === 'full') return true;
  if (mergedQualifier === 'self') return appliesSelfPermission(context);
  return appliesScopedPermission(context);
}

/** Checks if the viewer may open a UI view (page or section). */
export function canAccessView(
  view: IUiView,
  viewerRoles: IAuthRole[] = getViewerRoles(),
): boolean {
  const mergedAccess = getMergedUiViewAccess(view, viewerRoles);
  return mergedAccess !== 'none';
}

/** Returns platform roles the viewer is allowed to assign to other users. */
export function getAssignablePlatformRoles(
  viewerRoles: IAuthRole[] = getViewerRoles(),
): PlatformRoleType[] {
  if (viewerRoles.includes(PLATFORM_ADMIN)) {
    return [...PLATFORM_ADMIN_ASSIGNABLE_ROLES];
  }

  if (viewerRoles.includes(PLATFORM_SUPPORT)) {
    return [...PLATFORM_SUPPORT_ASSIGNABLE_ROLES];
  }

  if (viewerRoles.includes(ORG_OWNER)) {
    return [...ORG_OWNER_ASSIGNABLE_ROLES];
  }

  return [];
}

/** True when the viewer can open the platform-role assignment UI. */
export function canAssignPlatformRoles(viewerRoles: IAuthRole[] = getViewerRoles()): boolean {
  return canPerformAction(PERMISSION_ACTION.assignPlatformRole, {}, viewerRoles)
    || canPerformAction(PERMISSION_ACTION.assignOrgAdminRole, {}, viewerRoles);
}
